import {
  Badge,
  Button,
  DialogFooter,
  DialogHeader,
  DialogSection,
  PageTabs,
  PageTabsList,
  PageTabsTrigger,
} from "@tellescope/ui";
import { useEventFormDialogContents, type EventFormInitialValues } from "../../../../hooks";
import { useTranslation } from "react-i18next";
import { UnavailableUsersWarningPopover } from "../../UnavailableUsersWarningDialog";
import { InfoSection } from "./InfoSection";
import { LocationSection } from "./LocationSection";
import { NotesSection } from "./NotesSection";
import { RemindersSection } from "./RemindersSection";
import { IntegrationsSection } from "./IntegrationsSection";

export interface EventFormDialogContentsProps {
  eventId?: string;
  onSuccess: () => void;
  onCancel: () => void;
  timezone?: string;
  initialFormValues?: EventFormInitialValues;
}

export const EventFormDialogContents = ({
  eventId,
  onSuccess,
  onCancel,
  timezone,
  initialFormValues,
}: EventFormDialogContentsProps) => {
  const { t } = useTranslation("calendar");
  const {
    isEditing,
    form,
    templates,
    templateOptions,
    locationOptions,
    calculatedEndDate,
    users,
    endusers,
    enduserSearch,
    setEnduserSearch,
    selectedTab,
    setSelectedTab,
    tabsList,
    event,
    reminders,
    setReminders,
    onSubmit,
    updateWarningDialogProps,
  } = useEventFormDialogContents({
    eventId,
    onSuccess,
    initialFormValues,
    timezone,
  });

  return (
    <>
      <DialogHeader data-testid="event-form-dialog" className="font-semibold p-4 pb-0">
        {isEditing ? t("Edit Event") : t("Create Event")}
        <PageTabs value={selectedTab} onValueChange={setSelectedTab} defaultValue={tabsList[0]?.label} className="h-10">
          <PageTabsList className="border-0">
            {tabsList.map((tab) => (
              <PageTabsTrigger key={`tab-${tab}`} value={tab.label} className="flex gap-2 items-center">
                <span className="flex gap-0.5">
                  {tab.label}
                  {tab.isRequired && <span className="text-red-500 text-xs">*</span>}
                </span>
                {tab.label === t("Reminders") && reminders?.length ? (
                  <Badge className="p-1 rounded-full text-xs" variant="secondary">
                    {reminders.length}
                  </Badge>
                ) : null}
              </PageTabsTrigger>
            ))}
          </PageTabsList>
        </PageTabs>
      </DialogHeader>
      <DialogSection className="p-4 overflow-auto h-[550px] max-h-80svh">
        {selectedTab === t("Info") ? (
          <InfoSection
            form={form}
            templates={templates}
            templateOptions={templateOptions}
            calculatedEndDate={calculatedEndDate}
            users={users}
            endusers={endusers}
            enduserSearch={enduserSearch}
            setEnduserSearch={setEnduserSearch}
            timezone={timezone}
          />
        ) : selectedTab === t("Location") ? (
          <LocationSection form={form} locationOptions={locationOptions} />
        ) : selectedTab === t("Notes") ? (
          <NotesSection event={event} form={form} />
        ) : selectedTab === t("Reminders") ? (
          <RemindersSection form={form} reminders={reminders} setReminders={setReminders} />
        ) : selectedTab === t("Integrations") ? (
          <IntegrationsSection form={form} />
        ) : null}
      </DialogSection>
      <DialogFooter className="bg-gray-100">
        <Button variant="outline" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <UnavailableUsersWarningPopover {...updateWarningDialogProps}>
          <Button data-testid="event-form-submit" onClick={onSubmit} disabled={!form.isValid}>
            {isEditing ? t("Save Changes") : t("Create Event")}
          </Button>
        </UnavailableUsersWarningPopover>
      </DialogFooter>
    </>
  );
};
